import React, { useState } from 'react';
import Modal from '../Modal';
import { useCreateFeeMutation, useUpdateFeeMutation } from '../../store/api/localApi';
import type { Fee } from '../../types/fee';

interface Props {
  studentId: string;
  fee?: Fee;
  onClose: () => void;
}

export const AddFeeModal: React.FC<Props> = ({ studentId, fee, onClose }) => {
  const [label, setLabel] = useState(fee?.label ?? '');
  const [amount, setAmount] = useState(fee ? String(fee.amount) : '');
  const [error, setError] = useState('');

  const [createFee, { isLoading: isCreating }] = useCreateFeeMutation();
  const [updateFee, { isLoading: isUpdating }] = useUpdateFeeMutation();
  const isLoading = isCreating || isUpdating;

  const canSubmit = !isLoading && label.trim().length > 0 && Number(amount) > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const parsedAmount = parseFloat(amount);
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      setError('Amount must be a positive number');
      return;
    }
    try {
      if (fee) {
        await updateFee({
          studentId,
          feeId: fee.id,
          label: label.trim(),
          amount: parsedAmount,
        }).unwrap();
      } else {
        await createFee({ studentId, label: label.trim(), amount: parsedAmount }).unwrap();
      }
      onClose();
    } catch (err: any) {
      setError(err?.data?.error || err?.data?.message || 'Failed to save fee');
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={fee ? 'Edit Fee' : 'Add Fee'}
      maxWidth="sm"
      testId={fee ? 'edit-fee-modal' : 'add-fee-modal'}
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && <p className="text-sm text-red-600">{error}</p>}

        <div>
          <label htmlFor="fee-label" className="block text-sm font-medium text-gray-700">
            Description
          </label>
          <input
            id="fee-label"
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Term 1 Tuition"
            maxLength={200}
            required
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2.5 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            data-testid="fee-label-input"
          />
        </div>

        <div>
          <label htmlFor="fee-amount" className="block text-sm font-medium text-gray-700">
            Amount (€)
          </label>
          <input
            id="fee-amount"
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0"
            min="0.01"
            step="0.01"
            required
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2.5 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            data-testid="fee-amount-input"
          />
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
            data-testid="add-fee-submit"
          >
            {isLoading ? 'Saving…' : fee ? 'Save changes' : 'Add fee'}
          </button>
        </div>
      </form>
    </Modal>
  );
};
